import { useMemo, useRef, RefObject } from 'react'

export interface ColumnWidthConfig {
  field: string
  headerText: string
  minWidth?: number
  maxWidth?: number
  maxWidthPercentage?: number
  padding?: number
}

export interface UseOptimalColumnsWidthOptions {
  data: any[]
  columns: ColumnWidthConfig[]
  fallbackFont?: string
  tableSelector?: string
}

export interface UseOptimalColumnsWidthResult {
  widths: Record<string, string>
  tableRef: RefObject<HTMLDivElement>
}

export const useOptimalColumnsWidth = ({ data, columns, fallbackFont = '14px Inter, ui-sans-serif, system-ui', tableSelector = '.euiTableRowCell' }: UseOptimalColumnsWidthOptions): UseOptimalColumnsWidthResult => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const tableRef = useRef<HTMLDivElement>(null)

  const widths = useMemo(() => {
    if (!data || data.length === 0 || !columns.length) {
      return columns.reduce((acc, col) => {
        acc[col.field] = `${(col.minWidth || 100) + (col.padding || 32)}px`
        return acc
      }, {} as Record<string, string>)
    }

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas')
    }

    const canvas = canvasRef.current
    const context = canvas.getContext('2d')
    if (!context) {
      return columns.reduce((acc, col) => {
        acc[col.field] = `${(col.minWidth || 100) + (col.padding || 32)}px`
        return acc
      }, {} as Record<string, string>)
    }

    let tableFont = fallbackFont

    if (tableRef.current) {
      const tableCell = tableRef.current.querySelector(tableSelector)
      if (tableCell) {
        const computedStyle = window.getComputedStyle(tableCell)
        const fontSize = computedStyle.fontSize
        const fontFamily = computedStyle.fontFamily
        const fontWeight = computedStyle.fontWeight
        tableFont = `${fontWeight} ${fontSize} ${fontFamily}`
      }
    }

    context.font = tableFont

    const result: Record<string, string> = {}
    const screenWidth = window.innerWidth

    columns.forEach(column => {
      const { field, headerText, minWidth = 100, maxWidth = 200, maxWidthPercentage = 0.15, padding = 32 } = column

      const headerWidth = context.measureText(headerText).width

      const fieldValues = data.map(item => {
        const value = field.split('.').reduce((obj, key) => obj?.[key], item)
        return value?.toString() || ''
      })

      const maxDataWidth = Math.max(0, ...fieldValues.map(value => context.measureText(value).width))

      const maxContentWidth = Math.max(headerWidth, maxDataWidth)
      const calculatedWidth = maxContentWidth + padding

      const dynamicMaxWidth = Math.min(maxWidth, screenWidth * maxWidthPercentage)

      result[field] = `${Math.max(minWidth, Math.min(calculatedWidth, dynamicMaxWidth))}px`
    })

    return result
  }, [data, columns, fallbackFont, tableSelector])

  return { widths, tableRef }
}
